import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { analyzeD9NavamshaQuality, analyzeD60Shashtiamsha } from '../../utils/d9QualityEngine';
import { Sparkles, Shield, X, HelpCircle, Layers, CheckCircle2, AlertTriangle, Lock } from 'lucide-react';

interface D9DeepAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  kundali: CompleteKundali;
  lang: Language;
  chartStyle?: ChartStyle;
  userEntitlement?: 'BASIC' | 'PREMIUM' | 'ULTRA_PREMIUM';
}

export const D9DeepAnalysisModal: React.FC<D9DeepAnalysisModalProps> = ({
  isOpen,
  onClose,
  kundali,
  lang,
  chartStyle = 'north',
  userEntitlement = 'PREMIUM'
}) => {
  if (!isOpen) return null;

  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'D9' | 'D60'>('D9');

  const d9Analysis = analyzeD9NavamshaQuality(kundali, lang);
  const d60Analysis = analyzeD60Shashtiamsha(kundali, true, lang);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-astro-bg border border-astro-border rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-astro-border flex items-center justify-between bg-[#FAF6ED]">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-astro-primary/10 text-astro-primary">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-serif text-astro-text">
                {isHi ? 'गहन नवांश (D9) एवं षष्ट्यंश (D60) विश्लेषण' : 'D9 Navamsha & D60 Shashtiamsha Deep Intelligence'}
              </h2>
              <p className="text-xs text-[#6B7280]">
                {isHi ? 'आत्मिक स्वरूप, वैवाहिक परिपक्वता एवं सूक्ष्म कर्म-संस्कार' : 'Soul Trajectory, Marriage Maturation & Karmic Potential'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#6B7280] hover:text-astro-text hover:bg-astro-border/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-astro-border px-6 bg-[#FAF6ED]/60">
          <button
            onClick={() => setActiveTab('D9')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-all ${
              activeTab === 'D9'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-[#6B7280] hover:text-astro-text'
            }`}
          >
            {isHi ? 'D9 नवांश (धार्मिक व आत्मिक शक्ति)' : 'D9 Navamsha (Dharmic & Relationship Strength)'}
          </button>
          <button
            onClick={() => setActiveTab('D60')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === 'D60'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-[#6B7280] hover:text-astro-text'
            }`}
          >
            <span>{isHi ? 'D60 षष्ट्यंश (सूक्ष्म कर्म चक्र)' : 'D60 Shashtiamsha (Subtle Karma Matrix)'}</span>
            {userEntitlement === 'BASIC' && (
              <Lock className="w-3.5 h-3.5 text-astro-accent" />
            )}
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-astro-text">
          {activeTab === 'D9' ? (
            <div className="space-y-6">
              {/* D9 Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#FAF6ED] p-4 rounded-xl border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">
                    {isHi ? 'D9 नवांश लग्न' : 'D9 Navamsha Lagna'}
                  </span>
                  <div className="text-xl font-bold text-astro-primary">{d9Analysis.d9LagnaSign}</div>
                  <p className="text-[11px] text-[#6B7280]">{isHi ? 'आंतरिक आत्म-स्वभाव' : 'Core Soul Direction'}</p>
                </div>
                <div className="bg-[#FAF6ED] p-4 rounded-xl border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">
                    {isHi ? 'D9 चंद्र राशि' : 'D9 Moon Sign'}
                  </span>
                  <div className="text-xl font-bold text-astro-primary">{d9Analysis.d9MoonSign}</div>
                  <p className="text-[11px] text-[#6B7280]">{isHi ? 'मानसिक परिपक्वता' : 'Subtle Intuition'}</p>
                </div>
                <div className="bg-[#FAF6ED] p-4 rounded-xl border border-astro-border space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">
                    {isHi ? 'वर्गोत्तम ग्रह' : 'Vargottama Grahas'}
                  </span>
                  <div className="text-xl font-bold text-astro-accent">
                    {d9Analysis.vargottamaPlanets.length > 0 ? d9Analysis.vargottamaPlanets.join(', ') : isHi ? 'कोई नहीं' : 'None'}
                  </div>
                  <p className="text-[11px] text-[#6B7280]">{isHi ? 'अक्षुण्ण शक्ति' : 'Pristine Strength'}</p>
                </div>
              </div>

              {/* Crucial Architectural Distinction: D1 Overall Archetype vs D9 Derived Qualities */}
              <div className="bg-amber-50/50 border border-amber-200/80 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                  <Sparkles className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'शास्त्रीय अंतर: बाह्य व्यक्तित्व (D1) बनाम आत्मिक परिपक्वता (D9)' : 'Classical Distinction: Outer Archetype (D1) vs Inner Quality (D9)'}</span>
                </div>
                <p className="text-xs text-amber-950/80 leading-relaxed">
                  {isHi ? d9Analysis.d9VsD1SynthesisHi : d9Analysis.d9VsD1Synthesis}
                </p>
              </div>

              {/* Dharmic & Relationship Tendencies */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="bg-white p-4 rounded-xl border border-astro-border space-y-2">
                  <strong className="text-astro-primary font-bold text-sm block">
                    {isHi ? 'धर्म व जीवन उद्देश्य (Dharma Orientation):' : 'Dharmic & Life Orientation:'}
                  </strong>
                  <p className="text-astro-secondary-text leading-relaxed">
                    {isHi ? d9Analysis.dharmicOrientationHi : d9Analysis.dharmicOrientation}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-astro-border space-y-2">
                  <strong className="text-astro-primary font-bold text-sm block">
                    {isHi ? 'वैवाहिक एवं साझेदारी परिपक्वता:' : 'Relationship & Marriage Maturity:'}
                  </strong>
                  <p className="text-astro-secondary-text leading-relaxed">
                    {isHi ? d9Analysis.relationshipMaturationTendencyHi : d9Analysis.relationshipMaturationTendency}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* D60 Accuracy Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <strong className="font-bold text-blue-900 block">
                    {isHi ? 'जन्म समय परिशुद्धता सूचना (Birth-Time Accuracy Note):' : 'Birth-Time Accuracy Notice:'}
                  </strong>
                  <p className="text-blue-800 leading-relaxed">
                    {isHi ? d60Analysis.accuracyNoticeHi : d60Analysis.accuracyNotice}
                  </p>
                </div>
              </div>

              {userEntitlement === 'BASIC' ? (
                <div className="bg-[#FAF6ED] p-8 rounded-xl border border-astro-border text-center space-y-3">
                  <Lock className="w-8 h-8 text-astro-accent mx-auto" />
                  <h4 className="text-base font-bold text-astro-text">
                    {isHi ? 'षष्ट्यंश (D60) विस्तृत चक्र केवल प्रीमियम उपयोगकर्ताओं हेतु' : 'Full D60 Shashtiamsha Matrix Available on Premium'}
                  </h4>
                  <p className="text-xs text-[#6B7280] max-w-md mx-auto">
                    {isHi ? 'षष्ट्यंश सूक्ष्म कर्म संस्कारों एवं पूर्वजन्म की ऊर्जा का गहन प्रकटीकरण करता है।' : 'Shashtiamsha reveals the subtlest karmic threads and past-life devata vibrations.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">
                    {isHi ? 'सूक्ष्म ग्रहीय देवता एवं संस्कार (D60 Deities):' : 'Subtle Planetary Deities & Karmic Vibrations:'}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {d60Analysis.subtlePlanetaryVibrations.map((v, idx) => (
                      <div key={idx} className="bg-white p-3.5 rounded-xl border border-astro-border space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-astro-primary">{v.planet}</span>
                          <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                            {isHi ? v.deityQualityHi : v.deityQuality}
                          </span>
                        </div>
                        <p className="text-astro-secondary-text">
                          {isHi ? v.karmicIndicationHi : v.karmicIndication}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-astro-border bg-[#FAF6ED] flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-bold bg-astro-primary text-white hover:bg-[#0E3534] transition-colors"
          >
            {isHi ? 'बंद करें' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};
